import { useCallback, useEffect, useState } from "react";
import type { CSSProperties } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "../utils/api";
import { useTeamStore } from "../state/teamStore";

const CARS = [
  { id: "nascar-trucks", label: "NASCAR Trucks" },
  { id: "nascar-oreilly", label: "O'Reilly (Xfinity)" },
  { id: "nascar-cup-next-gen", label: "Cup Next Gen" },
];

interface EditItem { label: string; guidance: string }
interface EditStage { label: string; items: EditItem[] }
interface EditTemplate { id?: string; name: string; carId: string; stages: EditStage[] }
interface TemplateDto {
  id: string; name: string; carId: string; version: number;
  stages: { label: string; items: { label: string; guidance: string | null }[] }[];
}

const blank = (): EditTemplate => ({ name: "", carId: "nascar-trucks", stages: [{ label: "Base Step 1", items: [{ label: "", guidance: "" }] }] });

export default function ChecklistTemplates() {
  const { teamId } = useParams<{ teamId: string }>();
  const setActiveTeam = useTeamStore((s) => s.setActiveTeam);
  const isAdmin = useTeamStore((s) => s.activeTeam?.myRole === "owner" || s.activeTeam?.myRole === "admin");
  useEffect(() => { if (teamId) setActiveTeam(teamId); }, [teamId, setActiveTeam]);
  const [templates, setTemplates] = useState<TemplateDto[]>([]);
  const [editing, setEditing] = useState<EditTemplate | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    if (!teamId) return;
    try { setTemplates(await api<TemplateDto[]>(`/api/checklist/templates?team_id=${teamId}`)); }
    catch { setTemplates([]); }
  }, [teamId]);
  useEffect(() => { void reload(); }, [reload]);

  const editExisting = (t: TemplateDto) =>
    setEditing({ id: t.id, name: t.name, carId: t.carId, stages: t.stages.map((s) => ({ label: s.label, items: s.items.map((i) => ({ label: i.label, guidance: i.guidance || "" })) })) });

  const save = async () => {
    if (!teamId || !editing) return;
    const stages = editing.stages
      .map((s) => ({ label: s.label.trim(), items: s.items.filter((i) => i.label.trim()).map((i) => ({ label: i.label.trim(), guidance: i.guidance.trim() || null })) }))
      .filter((s) => s.label && s.items.length);
    if (!editing.name.trim() || !stages.length) { setError("Add a name and at least one stage with an item."); return; }
    setSaving(true); setError(null);
    try {
      if (editing.id) {
        await api(`/api/checklist/templates/${editing.id}`, { method: "PUT", body: JSON.stringify({ name: editing.name, carId: editing.carId, stages }) });
      } else {
        await api("/api/checklist/templates", { method: "POST", body: JSON.stringify({ teamId, carId: editing.carId, name: editing.name, stages }) });
      }
      setEditing(null);
      await reload();
    } catch (e: any) { setError(e?.message || "Save failed"); }
    finally { setSaving(false); }
  };

  const del = async (id: string) => {
    try { await api(`/api/checklist/templates/${id}`, { method: "DELETE" }); await reload(); }
    catch (e: any) { setError(e?.message || "Delete failed"); }
  };

  // ── helpers to mutate the editing structure immutably ──
  const upd = (fn: (e: EditTemplate) => void) => setEditing((prev) => { if (!prev) return prev; const next = structuredClone(prev); fn(next); return next; });

  return (
    <div className="page page--wide">
      <Link to={`/teams/${teamId}/settings`} className="back-link">&larr; Back to team settings</Link>
      <header className="editorial-header">
        <span className="eyebrow">Setup checklists</span>
        <h1 className="editorial-title">Checklist templates</h1>
        <p style={st.lede}>The pre-track verification list your team runs on each car. Authored once here, started fresh each week from the Checklist tab. Private to your team.</p>
      </header>

      {error && <div style={st.error}>{error}</div>}

      {!isAdmin && (
        <div style={st.muted}>Only team owners and admins can edit the checklist templates. You can run the per-week checklist from each week's Checklist tab.</div>
      )}

      {!editing && (
        <>
          {isAdmin && (
            <button className="btn btn-primary" onClick={() => setEditing(blank())} style={{ marginBottom: "1.25rem" }}>New template</button>
          )}
          {templates.length === 0 ? (
            <div style={st.muted}>{isAdmin ? "No templates yet. Create one to start checklists for your cars." : "Your team hasn't set up any checklist templates yet."}</div>
          ) : (
            <div style={st.list}>
              {templates.map((t) => (
                <div key={t.id} className="card" style={st.row}>
                  <div>
                    <div style={st.rowName}>{t.name}</div>
                    <div style={st.rowMeta}>{CARS.find((c) => c.id === t.carId)?.label || t.carId} · {t.stages.length} stages · {t.stages.reduce((n, s) => n + s.items.length, 0)} items</div>
                  </div>
                  {isAdmin && (
                    <div style={st.rowActions}>
                      <button className="btn btn-ghost" style={st.sm} onClick={() => editExisting(t)}>Edit</button>
                      <button className="btn btn-ghost" style={{ ...st.sm, color: "var(--flag-red)" }} onClick={() => del(t.id)}>Delete</button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {editing && (
        <div className="card" style={st.editor}>
          <div style={st.editTop}>
            <input className="input" placeholder="Template name (e.g. Truck Base)" value={editing.name} onChange={(e) => upd((d) => { d.name = e.target.value; })} style={{ flex: 1, minWidth: "12rem" }} />
            <select className="select" value={editing.carId} onChange={(e) => upd((d) => { d.carId = e.target.value; })}>
              {CARS.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
            </select>
          </div>

          {editing.stages.map((stage, si) => (
            <div key={si} style={st.stageBox}>
              <div style={st.stageHead}>
                <input className="input" placeholder="Stage label (e.g. Base Step 1)" value={stage.label} onChange={(e) => upd((d) => { d.stages[si].label = e.target.value; })} style={{ flex: 1, fontWeight: 600 }} />
                <button className="btn btn-ghost" style={{ ...st.sm, color: "var(--flag-red)" }} onClick={() => upd((d) => { d.stages.splice(si, 1); })}>Remove stage</button>
              </div>
              {stage.items.map((item, ii) => (
                <div key={ii} style={st.itemRow}>
                  <input className="input" placeholder="Item (e.g. RF Camber)" value={item.label} onChange={(e) => upd((d) => { d.stages[si].items[ii].label = e.target.value; })} style={{ flex: 1 }} />
                  <input className="input" placeholder="What to do (optional)" value={item.guidance} onChange={(e) => upd((d) => { d.stages[si].items[ii].guidance = e.target.value; })} style={{ flex: 2 }} />
                  <button className="btn btn-ghost" style={st.sm} title="Remove item" aria-label="Remove item" onClick={() => upd((d) => { d.stages[si].items.splice(ii, 1); })}>×</button>
                </div>
              ))}
              <button className="btn btn-ghost" style={st.sm} onClick={() => upd((d) => { d.stages[si].items.push({ label: "", guidance: "" }); })}>+ Add item</button>
            </div>
          ))}

          <button className="btn btn-ghost" style={{ ...st.sm, alignSelf: "flex-start" }} onClick={() => upd((d) => { d.stages.push({ label: "", items: [{ label: "", guidance: "" }] }); })}>+ Add stage</button>

          <div style={st.editActions}>
            <button className="btn btn-secondary" onClick={() => { setEditing(null); setError(null); }}>Cancel</button>
            <button className="btn btn-primary" onClick={save} disabled={saving}>{saving ? "Saving…" : "Save template"}</button>
          </div>
        </div>
      )}
    </div>
  );
}

const st: Record<string, CSSProperties> = {
  lede: { color: "var(--text-secondary)", fontSize: "0.92rem", lineHeight: 1.6, marginTop: "0.5rem", maxWidth: "44rem" },
  error: { marginBottom: "1rem", padding: "0.6rem 0.8rem", background: "var(--flag-red-soft)", border: "1px solid var(--flag-red)", color: "var(--flag-red)", borderRadius: "var(--r-md)", fontSize: "0.85rem" },
  muted: { color: "var(--text-muted)", fontSize: "0.92rem" },
  list: { display: "flex", flexDirection: "column", gap: "0.6rem" },
  row: { display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0.9rem 1.1rem", gap: "1rem", flexWrap: "wrap" },
  rowName: { fontFamily: "var(--font-display)", fontWeight: 700, fontSize: "1rem", color: "var(--text-strong)" },
  rowMeta: { color: "var(--text-secondary)", fontSize: "0.82rem", marginTop: "0.15rem" },
  rowActions: { display: "flex", gap: "0.4rem" },
  sm: { padding: "0.3rem 0.7rem", fontSize: "0.8125rem" },
  editor: { padding: "1.5rem", display: "flex", flexDirection: "column", gap: "1rem" },
  editTop: { display: "flex", gap: "0.75rem", flexWrap: "wrap" },
  stageBox: { border: "1px solid var(--rule)", borderRadius: "var(--r-md)", padding: "1rem", display: "flex", flexDirection: "column", gap: "0.5rem" },
  stageHead: { display: "flex", gap: "0.6rem", alignItems: "center" },
  itemRow: { display: "flex", gap: "0.5rem", alignItems: "center" },
  editActions: { display: "flex", gap: "0.75rem", justifyContent: "flex-end", marginTop: "0.5rem" },
};
